/**
 * Client-side Company Score recalculation from Company Parameters only.
 * Mirrors customer-service monitoring_risk_score.go for param-driven metrics.
 * Cohort-derived rows (YoY, CAC payback, ROAS vector, CAC prediction) are kept
 * from the last server scorecard when present.
 */

import {
  BALANCE_SHEET_EXPLANATION,
  CASH_BALANCE_EXPLANATION,
  LIQUID_VALUE_EXPLANATION,
  REVENUE_GROWTH_YOY_EXPLANATION,
  RUNOFF_EXPLANATION,
  RUNWAY_EXPLANATION,
  UA_PREDICTION_EXPLANATION,
} from '../../../QuickCashflowModel/calc/cloneExcelWorksheet';
import { computeUwScorecardTotals } from '../../../QuickCashflowModel/calc/underwritingScorecardScoring';

export const MONITORING_COMPANY_SCORE_SPECS = [
  {
    key: 'runway',
    name: 'Runway',
    kind: 'higher',
    t3: 18,
    t2: 9,
    labelScore3: '>18 months',
    labelScore2: '9–18 months',
    labelScore1: '<9 months',
    explanation: RUNWAY_EXPLANATION,
  },
  {
    key: 'runoff_without_cash',
    name: 'Runoff',
    kind: 'higher',
    t3: 18,
    t2: 9,
    labelScore3: '>18 months',
    labelScore2: '9–18 months',
    labelScore1: '<9 months',
    explanation: RUNOFF_EXPLANATION,
  },
  {
    key: 'liquid_value',
    name: 'Liquid Value',
    kind: 'higher',
    t3: 15,
    t2: 9,
    labelScore3: '>15x monthly OPEX in liquid assets',
    labelScore2: '9–15x monthly OPEX',
    labelScore1: '<9x monthly OPEX',
    explanation: LIQUID_VALUE_EXPLANATION,
  },
  {
    key: 'revenue_growth_yoy',
    name: 'Revenue Growth (YoY)',
    kind: 'preserve',
    labelScore3: '>25% YoY',
    labelScore2: '5–25% YoY',
    labelScore1: '<5% YoY',
    explanation: REVENUE_GROWTH_YOY_EXPLANATION,
  },
  {
    key: 'balance_sheet',
    name: 'Balance Sheet',
    kind: 'higher',
    t3: 1.5,
    t2: 1.0,
    labelScore3: 'Current ratio >1.5',
    labelScore2: 'Current ratio 1.0–1.5',
    labelScore1: 'Current ratio <1.0',
    explanation: BALANCE_SHEET_EXPLANATION,
  },
  {
    key: 'stress_testing',
    name: 'Stress Testing',
    kind: 'qualitative',
    labelScore3: 'Can easily service debt under stress scenarios',
    labelScore2: 'Some challenges under moderate stress',
    labelScore1: 'Cannot service debt under stress',
    explanation: 'Qualitative assessment of debt service under stress scenarios.',
  },
  {
    key: 'cash_balance',
    name: 'Cash Balance',
    kind: 'higher',
    t3: 18,
    t2: 6,
    labelScore3: '>18x monthly OPEX',
    labelScore2: '6–18x monthly OPEX',
    labelScore1: '<6x monthly OPEX',
    explanation: CASH_BALANCE_EXPLANATION,
  },
  {
    key: 'cac_payback',
    name: 'CAC Payback',
    kind: 'preserve',
    labelScore3: '<4 months',
    labelScore2: '4–11 months',
    labelScore1: '>12 months',
    explanation:
      'Months until the 6-month avg cumulative gross ROAS (last 6 cohorts from Sales cohort data) reaches 100%. Reported 1-indexed (period 0 → 1 month).',
  },
  {
    key: 'roas_vector',
    name: 'ROAS Vector',
    kind: 'preserve',
    labelScore3: '<4 months to 50% ROAS',
    labelScore2: '4–12 months',
    labelScore1: '>12 months or never',
    explanation:
      'Months until the 6-month avg cumulative gross ROAS (last 6 cohorts from Sales cohort data) reaches 50%. Reported 1-indexed (period 0 → 1 month).',
  },
  {
    key: 'ua_prediction',
    name: 'CAC Prediction',
    kind: 'preserve',
    labelScore3: '≥1.5× — high room to scale spend before ROAS floor',
    labelScore2: '1.0–1.5× — moderate headroom',
    labelScore1: '<1.0× — spend exceeds Hill ROAS floor',
    explanation: UA_PREDICTION_EXPLANATION,
  },
  {
    key: 'investors',
    name: 'Investors',
    kind: 'qualitative',
    labelScore3: 'Top-tier VCs / strategic investors with strong track record',
    labelScore2: 'Medium-tier / regional funds',
    labelScore1: 'Unknown investors / bootstrapped',
    explanation: 'Qualitative assessment of investor quality.',
  },
  {
    key: 'management',
    name: 'Management',
    kind: 'qualitative',
    labelScore3: 'Seasoned team, scaled companies before, domain experts',
    labelScore2: 'Experienced but haven\'t scaled to this level',
    labelScore1: 'Little relevant experience',
    explanation: 'Qualitative assessment of management team.',
  },
];

const SPECS_BY_KEY = Object.fromEntries(
  MONITORING_COMPANY_SCORE_SPECS.map((spec) => [spec.key, spec]),
);

const CONFIG_DRIVEN_SCORE_KEYS = new Set([
  'runway',
  'runoff_without_cash',
  'liquid_value',
  'revenue_growth_yoy',
  'balance_sheet',
  'cash_balance',
  'cac_payback',
  'roas_vector',
  'ua_prediction',
]);

export function indexMonitoringScorecardMetrics(scorecardConfig) {
  const map = new Map();
  (scorecardConfig?.metrics || []).forEach((metric) => {
    if (metric?.key) {
      map.set(metric.key, metric);
    }
  });
  return map;
}

function specWithScorecardConfig(spec, metric) {
  if (!spec?.key || !metric || !CONFIG_DRIVEN_SCORE_KEYS.has(spec.key)) {
    return spec;
  }
  const next = {
    ...spec,
    name: metric.label || spec.name,
    labelScore3: metric.labelScore3 || spec.labelScore3,
    labelScore2: metric.labelScore2 || spec.labelScore2,
    labelScore1: metric.labelScore1 || spec.labelScore1,
  };
  if (spec.kind === 'qualitative' || spec.kind === 'preserve') {
    return next;
  }
  if (metric.kind === 'numeric_lower_better') {
    next.kind = 'lower';
  } else if (metric.kind === 'numeric_higher_better') {
    next.kind = 'higher';
  }
  const highCutoff = Number(metric.thresholdScore3);
  const moderateCutoff = Number(metric.thresholdScore2);
  if (Number.isFinite(highCutoff)) {
    next.t3 = highCutoff;
  }
  if (Number.isFinite(moderateCutoff)) {
    next.t2 = moderateCutoff;
  }
  return next;
}

/** Tooltips for Company Parameters — how each auto value is derived. */
export const MONITORING_PARAM_TOOLTIPS = {
  currentCash:
    'Sum of bank account balances from underwriting bank transactions, excluding accounts whose name contains “credit”. Editable manually.',
  netBurn:
    'From accounting balance sheet cash: average of adjacent month-to-month cash differences over the last 4 months (signed, no absolute value). Editable manually.',
  monthlyOpex:
    'From accounting P&L operating expenses: average of the absolute opex totals over the prior 3 months (skips the current/latest month), excluding any accounts whose label contains “marketing”. Editable manually.',
  debt:
    'Sum of latest-month balance sheet liability accounts whose label contains “debt”. Editable manually.',
  totalAssets:
    'Latest-month total assets from accounting balance sheet. Editable manually.',
  totalLiabilities:
    'Latest-month total liabilities from accounting balance sheet. Editable manually.',
  safe:
    'Sum of latest-month balance sheet accounts whose label contains “safe”. Editable manually.',
  predictedRunoff12Months:
    'Sales page runoff (runoff_prospective) at month/period 12 — gross total with refunds. Editable manually (manual overrides are kept on refetch).',
  stressTestingScore: 'Qualitative score (1–3). Set manually or left blank.',
  investorsScore:
    'Qualitative score (1–3). Auto-filled from business info when available; editable manually.',
  managementScore:
    'Qualitative score (1–3). Auto-filled from business info when available; editable manually.',
};

function scoreHigher(value, t3, t2) {
  if (value >= t3) return 3;
  if (value >= t2) return 2;
  return 1;
}

function scoreFromConfiguredSpec(value, spec) {
  if (spec.kind === 'lower') {
    if (value < spec.t3) return 3;
    if (value < spec.t2) return 2;
    return 1;
  }
  return scoreHigher(value, spec.t3, spec.t2);
}

function formatGroupedNumber(value, fractionDigits = 2) {
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatCompanyScoreValue(key, value) {
  switch (key) {
    case 'runway':
      return `~${formatGroupedNumber(value, 2)} mo`;
    case 'runoff_without_cash':
    case 'liquid_value':
    case 'cash_balance':
      return `~${formatGroupedNumber(value, 2)}x OPEX`;
    case 'balance_sheet':
    case 'ua_prediction':
      return formatGroupedNumber(value, 2);
    case 'revenue_growth_yoy':
      return `${formatGroupedNumber(value, 2)}%`;
    case 'cac_payback':
    case 'roas_vector':
      return `~${formatGroupedNumber(value, 2)} mo`;
    default:
      return formatGroupedNumber(value, 2);
  }
}

function finiteNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function qualitativeScore(value) {
  const n = finiteNumber(value);
  if (n == null) return null;
  const rounded = Math.round(n);
  return rounded >= 1 && rounded <= 3 ? rounded : null;
}

function firstUwLabel(pctOfMax) {
  if (pctOfMax == null || !Number.isFinite(pctOfMax)) {
    return 'Enter Ratings';
  }
  if (pctOfMax >= 0.75) return 'HIGH CONVICTION';
  if (pctOfMax >= 0.5) return 'MODERATE';
  return 'LOW / PASS';
}

function existingRowByKey(existingRows) {
  const map = new Map();
  (existingRows || []).forEach((row) => {
    if (row?.key) {
      map.set(row.key, row);
    }
  });
  return map;
}

function formatParamCurrency(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return null;
  }
  return `$${Math.round(Number(value)).toLocaleString('en-US')}`;
}

export function scoreToRating(score) {
  if (score === 3) return 'High';
  if (score === 2) return 'Moderate';
  if (score === 1) return 'Low';
  return null;
}

export function buildMonitoringMetricParamsText(key, params, scorecardRow) {
  const cash = finiteNumber(params?.currentCash);
  const netBurn = finiteNumber(params?.netBurn);
  const opex = finiteNumber(params?.monthlyOpex);
  const debt = finiteNumber(params?.debt);
  const assets = finiteNumber(params?.totalAssets);
  const liabilities = finiteNumber(params?.totalLiabilities);
  const safe = finiteNumber(params?.safe);
  const runoff12 = finiteNumber(params?.predictedRunoff12Months);

  const parts = [];
  const pushCurrency = (label, value) => {
    const formatted = formatParamCurrency(value);
    if (formatted) {
      parts.push(`${label}=${formatted}`);
    }
  };
  const pushText = (label, value) => {
    if (value != null && value !== '') {
      parts.push(`${label}=${value}`);
    }
  };

  switch (key) {
    case 'runway':
      pushCurrency('beginning cash', cash);
      if (netBurn != null) {
        pushText('net burn', `${formatParamCurrency(netBurn)}/mo`);
      }
      break;
    case 'runoff_without_cash':
      pushCurrency('12 month predicted runoff', runoff12);
      pushCurrency('monthly OPEX', opex);
      break;
    case 'liquid_value':
      pushCurrency('beginning cash', cash);
      pushCurrency('12 month predicted runoff', runoff12);
      pushCurrency('debt', debt);
      pushCurrency('monthly OPEX', opex);
      break;
    case 'revenue_growth_yoy':
      pushText('value', scorecardRow?.valueText);
      break;
    case 'balance_sheet':
      pushCurrency('total assets', assets);
      pushCurrency('total liabilities', liabilities);
      pushCurrency('SAFE', safe);
      break;
    case 'cash_balance':
      pushCurrency('beginning cash', cash);
      pushCurrency('monthly OPEX', opex);
      break;
    case 'cac_payback':
      pushText('ROAS threshold', '100%');
      pushText('value', scorecardRow?.valueText);
      break;
    case 'roas_vector':
      pushText('ROAS threshold', '50%');
      pushText('value', scorecardRow?.valueText);
      break;
    case 'ua_prediction':
      pushText('value', scorecardRow?.valueText);
      break;
    case 'stress_testing':
      pushText('score', params?.stressTestingScore);
      break;
    case 'investors':
      pushText('score', params?.investorsScore);
      break;
    case 'management':
      pushText('score', params?.managementScore);
      break;
    default:
      break;
  }

  return parts.length ? parts.join(', ') : '—';
}

function fmtWeightPercent(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return '—';
  }
  return Number(value).toFixed(2);
}

function fmtWeightedScore(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return '—';
  }
  return Number(value).toFixed(4);
}

/**
 * Builds Values + Explanations view rows (First UW / Quick Cashflow style).
 */
export function buildMonitoringScorecardViewModel(scorecardRows, params, scorecardConfig = null) {
  const metricsByKey = indexMonitoringScorecardMetrics(scorecardConfig);
  const scoringRows = (scorecardRows || []).map((row) => {
    const spec = specWithScorecardConfig(
      SPECS_BY_KEY[row.key] || { key: row.key },
      metricsByKey.get(row.key),
    );
    return {
      key: row.key,
      metricLabel: row.name || spec.name || row.key,
      valueText: row.valueText || '',
      score: row.score ?? null,
      rating: scoreToRating(row.score),
      paramsText: buildMonitoringMetricParamsText(row.key, params, row),
      labelScore3: spec.labelScore3 || '—',
      labelScore2: spec.labelScore2 || '—',
      labelScore1: spec.labelScore1 || '—',
      explanation: spec.explanation || '—',
    };
  });

  const totals = computeUwScorecardTotals(scoringRows);

  const valueRows = totals.rowsWithWeights.map((row) => ({
    key: row.key,
    metric: row.metricLabel,
    value: row.valueText || '—',
    score: row.score ?? '—',
    weightPercent: fmtWeightPercent(row.weightPercent),
    weightedScore: fmtWeightedScore(row.weightedScore),
  }));

  const explanationRows = scoringRows.map((row) => ({
    key: row.key,
    metric: row.metricLabel,
    high: row.labelScore3,
    moderate: row.labelScore2,
    low: row.labelScore1,
    explanation: row.explanation,
    calculationInputs: row.paramsText,
  }));

  return { valueRows, explanationRows, totals };
}

/**
 * @param {object} params Company Parameters draft
 * @param {object[]} [existingScorecard] Last server scorecard (for preserve keys)
 * @returns {{
 *   cashflowScorecard: object[],
 *   riskScoreFinalPct: number|null,
 *   riskScoreFinalLabel: string,
 *   scorecardFinalScore: number|null,
 *   liquidityCash: number|null,
 * }}
 */
export function recalculateMonitoringCompanyScoreFromParams(
  params,
  existingScorecard = [],
  scorecardConfig = null,
) {
  const cash = finiteNumber(params?.currentCash) ?? 0;
  const hasCash = finiteNumber(params?.currentCash) != null;
  const netBurn = finiteNumber(params?.netBurn);
  const opexAbs = Math.abs(finiteNumber(params?.monthlyOpex) ?? 0);
  const debt = finiteNumber(params?.debt) ?? 0;
  const assets = finiteNumber(params?.totalAssets);
  const liabilities = finiteNumber(params?.totalLiabilities);
  const safe = finiteNumber(params?.safe) ?? 0;
  const runoff12 = finiteNumber(params?.predictedRunoff12Months) ?? 0;

  const numeric = {
    runway: null,
    runoff_without_cash: null,
    liquid_value: null,
    balance_sheet: null,
    cash_balance: null,
  };

  if (netBurn != null && Math.abs(netBurn) > 0 && hasCash) {
    numeric.runway = cash / Math.abs(netBurn);
  }
  if (opexAbs > 0) {
    numeric.runoff_without_cash = runoff12 / opexAbs;
    numeric.liquid_value = (cash + runoff12 - debt) / opexAbs;
    if (hasCash) {
      numeric.cash_balance = cash / opexAbs;
    }
  }
  if (assets != null && liabilities != null) {
    const adj = liabilities - safe;
    if (adj > 0) {
      numeric.balance_sheet = assets / adj;
    }
  }

  const previous = existingRowByKey(existingScorecard);
  const metricsByKey = indexMonitoringScorecardMetrics(scorecardConfig);
  const scores = [];
  const cashflowScorecard = MONITORING_COMPANY_SCORE_SPECS.map((baseSpec) => {
    const spec = specWithScorecardConfig(baseSpec, metricsByKey.get(baseSpec.key));
    if (spec.kind === 'preserve') {
      const prev = previous.get(spec.key);
      if (prev) {
        if (prev.score != null && Number.isFinite(Number(prev.score))) {
          scores.push(Number(prev.score));
        }
        return {
          key: spec.key,
          name: spec.name,
          valueText: prev.valueText || '',
          score: prev.score ?? null,
        };
      }
      return { key: spec.key, name: spec.name, valueText: '', score: null };
    }

    if (spec.kind === 'qualitative') {
      let score = null;
      if (spec.key === 'stress_testing') {
        score = qualitativeScore(params?.stressTestingScore);
      } else if (spec.key === 'investors') {
        score = qualitativeScore(params?.investorsScore);
      } else if (spec.key === 'management') {
        score = qualitativeScore(params?.managementScore);
      }
      if (score != null) {
        scores.push(score);
        return {
          key: spec.key,
          name: spec.name,
          valueText: String(score),
          score,
        };
      }
      return { key: spec.key, name: spec.name, valueText: '', score: null };
    }

    const value = numeric[spec.key];
    if (value == null || !Number.isFinite(value)) {
      return { key: spec.key, name: spec.name, valueText: '', score: null };
    }
    const score = scoreFromConfiguredSpec(value, spec);
    scores.push(score);
    return {
      key: spec.key,
      name: spec.name,
      valueText: formatCompanyScoreValue(spec.key, value),
      score,
    };
  });

  if (scores.length === 0) {
    return {
      cashflowScorecard,
      riskScoreFinalPct: null,
      riskScoreFinalLabel: 'Enter Ratings',
      scorecardFinalScore: null,
      liquidityCash: hasCash ? cash : null,
    };
  }

  // Prefer equal-weight % of max (same as First UW / Quick Cashflow).
  const view = buildMonitoringScorecardViewModel(cashflowScorecard, params, scorecardConfig);
  const mean = scores.reduce((sum, s) => sum + s, 0) / scores.length;
  const pct = view.totals.scorePercentOfMax ?? mean / 3;
  return {
    cashflowScorecard,
    riskScoreFinalPct: pct,
    riskScoreFinalLabel: firstUwLabel(pct),
    scorecardFinalScore: Math.round(mean),
    liquidityCash: hasCash ? cash : null,
  };
}

const COMPANY_SCORE_RATING_BY_SCORE = { 3: 'High', 2: 'Moderate', 1: 'Low' };
const COMPANY_SCORE_SCORE_BY_RATING = { High: 3, Moderate: 2, Low: 1 };

function companyScoreRating(score) {
  if (score == null) {
    return '—';
  }
  return COMPANY_SCORE_RATING_BY_SCORE[score] ?? '—';
}

function companyScoreRatingToScore(rating) {
  if (rating == null) {
    return null;
  }
  return COMPANY_SCORE_SCORE_BY_RATING[rating] ?? null;
}

function companyScoreParseNumber(text) {
  if (text == null) {
    return null;
  }
  const n = Number(String(text).trim());
  return Number.isFinite(n) ? n : null;
}

// Mirrors monitoringCompanyScoreChangeValue / NumericChange in
// customer-service: +N / -N, and "—" for no change or missing data.
function companyScoreDelta(current, prior) {
  if (current == null || prior == null) {
    return '—';
  }
  const d = current - prior;
  if (d > 0) {
    return `+${d}`;
  }
  if (d < 0) {
    return `${d}`;
  }
  return '—';
}

function isNormalizedFooter(metric) {
  return /normalized|1-100/i.test(metric || '');
}

/**
 * Rebuilds the "This Week" column, week-over-week change, and the header
 * grade/score of the Company Score slide snapshot from a client-side recalc,
 * without a server round-trip. The "Last Week" values are preserved from the
 * existing snapshot (a this-week edit does not change last week's numbers).
 *
 * Mirrors buildMonitoringCompanyScoreSlide in customer-service so the downloaded
 * deck matches the on-screen recalculated scorecard.
 */
export function patchCompanyScoreSnapshot(companyScoreSnapshot, scored) {
  if (!companyScoreSnapshot || !scored) {
    return companyScoreSnapshot;
  }
  const rows = Array.isArray(companyScoreSnapshot.rows)
    ? companyScoreSnapshot.rows
    : [];

  const newByName = new Map();
  let sum = 0;
  let ratedCount = 0;
  (scored.cashflowScorecard || []).forEach((row) => {
    if (row?.name) {
      newByName.set(row.name, row);
    }
    if (row?.score != null && Number.isFinite(Number(row.score))) {
      sum += Number(row.score);
      ratedCount += 1;
    }
  });

  const pct = scored.riskScoreFinalPct;
  const normalizedValue = pct != null ? Math.round(pct * 100) : null;
  const normalizedText = normalizedValue != null ? String(normalizedValue) : '—';

  const patchedRows = rows.map((row) => {
    if (row?.footerRow) {
      if (isNormalizedFooter(row?.metric)) {
        return {
          ...row,
          thisWeek: normalizedText,
          change: companyScoreDelta(
            normalizedValue,
            companyScoreParseNumber(row?.lastWeek),
          ),
        };
      }
      const sumValue = ratedCount > 0 ? sum : null;
      return {
        ...row,
        thisWeek: sumValue != null ? String(sumValue) : '—',
        change: companyScoreDelta(
          sumValue,
          companyScoreParseNumber(row?.lastWeek),
        ),
      };
    }

    const newRow = newByName.get(row?.metric);
    const thisScore = newRow?.score ?? null;
    const thisWeek =
      thisScore != null ? companyScoreRating(thisScore) : newRow?.valueText || '—';
    return {
      ...row,
      thisWeek,
      change: companyScoreDelta(
        thisScore,
        companyScoreRatingToScore(row?.lastWeek),
      ),
    };
  });

  return {
    ...companyScoreSnapshot,
    overallGrade: (scored.riskScoreFinalLabel || '').trim() || '—',
    companyScore: normalizedText,
    rows: patchedRows,
  };
}

/**
 * Updates Liquidity Test covenant row value/check from Current Cash.
 */
export function applyLiquidityCashToResultRows(resultRows, liquidityCash, minCashBalance) {
  if (!Array.isArray(resultRows)) {
    return resultRows;
  }
  return resultRows.map((row) => {
    if (row?.kpi !== 'Liquidity Test') {
      return row;
    }
    let check = 'none';
    if (
      liquidityCash != null &&
      Number.isFinite(Number(liquidityCash)) &&
      minCashBalance != null &&
      Number.isFinite(Number(minCashBalance))
    ) {
      check = Number(liquidityCash) >= Number(minCashBalance) ? 'pass' : 'fail';
    }
    return {
      ...row,
      value: liquidityCash,
      check,
    };
  });
}
